// src/pages/NetworkTopologyLight.jsx
// Light Mode Network Topology Visualization for Presentations
// Optimized for screenshots and embedding in slide decks

import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  Panel,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { edgeNodesService } from '../services/edge-nodes';
import { eventsService } from '../services/events';

// ════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ════════════════════════════════════════════════════════════════════════════

const isNodeOnline = (node) => {
  if (!node.last_heartbeat) return false;
  
  let heartbeatStr = node.last_heartbeat;
  if (!heartbeatStr.endsWith('Z') && !heartbeatStr.includes('+')) {
    heartbeatStr += 'Z';
  }
  
  const lastHeartbeat = new Date(heartbeatStr).getTime();
  const nowUTC = Date.now();
  const diffMinutes = (nowUTC - lastHeartbeat) / (1000 * 60);
  
  return diffMinutes <= 30;
};

const getNodeStatus = (node) => {
  if (!node) return 'unknown';
  return node.status || 'unknown';
};

// ════════════════════════════════════════════════════════════════════════════
// CUSTOM NODE COMPONENTS - LIGHT THEME
// ════════════════════════════════════════════════════════════════════════════

// Cloud/Management Plane Node - Light
const CloudNode = ({ data }) => (
  <div className="relative group">
    <Handle
      type="source"
      position={Position.Bottom}
      className="!w-3 !h-3 !bg-cyan-500 !border-2 !border-white"
    />
    
    <div className="relative bg-white border-2 border-cyan-400 rounded-2xl p-6 min-w-[200px] shadow-xl"
         style={{ boxShadow: '0 4px 20px rgba(6, 182, 212, 0.2)' }}>
      {/* Status indicator */}
      <div className="absolute -top-1.5 -right-1.5 w-4 h-4">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
        <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-500 shadow-md" />
      </div>
      
      {/* Icon */}
      <div className="flex items-center justify-center mb-3">
        <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl flex items-center justify-center border border-cyan-300">
          <svg className="w-8 h-8 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
          </svg>
        </div>
      </div>
      
      {/* Label */}
      <div className="text-center">
        <div className="text-sm font-semibold text-slate-800 tracking-wide">{data.label}</div>
        <div className="text-xs text-slate-500 mt-1 font-mono">{data.sublabel}</div>
      </div>
      
      {/* Stats bar */}
      <div className="mt-4 pt-3 border-t border-slate-200 flex justify-center gap-4">
        <div className="text-center">
          <div className="text-lg font-bold text-emerald-600">{data.edgeCount || 0}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">Agents</div>
        </div>
        <div className="text-center">
          <div className="text-lg font-bold text-blue-600">{data.assetCount || 0}</div>
          <div className="text-[10px] text-slate-500 uppercase tracking-wider">Devices</div>
        </div>
      </div>
    </div>
  </div>
);

// Edge Node/Gateway Node - Light
const EdgeGatewayNode = ({ data }) => {
  const isOnline = data.status === 'Online';
  
  return (
    <div className="relative group cursor-pointer">
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-emerald-500 !border-2 !border-white"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        className="!w-3 !h-3 !bg-blue-500 !border-2 !border-white"
      />
      
      <div className={`relative bg-white border-2 ${isOnline ? 'border-emerald-400' : 'border-red-400'} rounded-xl p-4 min-w-[220px] shadow-lg transition-all duration-300 group-hover:shadow-xl`}
           style={{ boxShadow: isOnline ? '0 4px 20px rgba(16, 185, 129, 0.15)' : '0 4px 20px rgba(239, 68, 68, 0.15)' }}>
        {/* Status dot */}
        <div className="absolute -top-1 -right-1 w-3 h-3">
          {isOnline && <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />}
          <span className={`relative inline-flex rounded-full h-3 w-3 ${isOnline ? 'bg-emerald-500' : 'bg-red-500'} shadow-md`} />
        </div>
        
        {/* Content */}
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isOnline ? 'bg-emerald-100' : 'bg-red-100'}`}>
            <svg className={`w-5 h-5 ${isOnline ? 'text-emerald-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-slate-800 truncate">{data.nodeId}</div>
            {data.hostname && data.hostname !== data.nodeId && (
              <div className="text-[10px] text-slate-500 font-mono truncate">{data.hostname}</div>
            )}
          </div>
        </div>
        
        {/* Stats row */}
        <div className="mt-3 pt-2 border-t border-slate-200 flex justify-between text-xs">
          <div>
            <span className="text-slate-500">Status</span>
            <div className={`font-semibold ${isOnline ? 'text-emerald-600' : 'text-red-600'}`}>{data.status}</div>
          </div>
          <div className="text-right">
            <span className="text-slate-500">Devices</span>
            <div className="font-semibold text-blue-600">{data.deviceCount || 0}</div>
          </div>
        </div>
        
        {/* IP Address */}
        {data.ipAddress && (
          <div className="mt-2 text-center">
            <span className="text-[10px] px-2 py-0.5 bg-slate-100 rounded-full text-cyan-600 font-mono border border-slate-200">
              {data.ipAddress}
            </span>
          </div>
        )}
        
        {/* Version tag */}
        {data.version && (
          <div className="mt-2 text-center">
            <span className="text-[9px] px-2 py-0.5 bg-slate-100 rounded-full text-slate-500 font-mono border border-slate-200">
              v{data.version}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

// Discovered Device Node - Light
const DeviceNode = ({ data }) => {
  const protocolColors = {
    modbus: { bg: 'bg-orange-100', border: 'border-orange-400', text: 'text-orange-600', badge: 'bg-orange-100 text-orange-700 border-orange-300' },
    opcua: { bg: 'bg-purple-100', border: 'border-purple-400', text: 'text-purple-600', badge: 'bg-purple-100 text-purple-700 border-purple-300' },
    tcp: { bg: 'bg-blue-100', border: 'border-blue-400', text: 'text-blue-600', badge: 'bg-blue-100 text-blue-700 border-blue-300' },
    s7: { bg: 'bg-green-100', border: 'border-green-400', text: 'text-green-600', badge: 'bg-green-100 text-green-700 border-green-300' },
    default: { bg: 'bg-slate-100', border: 'border-slate-400', text: 'text-slate-600', badge: 'bg-slate-100 text-slate-700 border-slate-300' },
  };
  
  const primaryProtocol = data.protocols?.[0] || 'default';
  const colors = protocolColors[primaryProtocol] || protocolColors.default;
  
  const hasViolations = data.policyViolations > 0;
  const borderColor = hasViolations ? 'border-red-400' : colors.border;

  return (
    <div className="relative group cursor-pointer">
      <Handle
        type="target"
        position={Position.Top}
        className="!w-3 !h-3 !bg-orange-500 !border-2 !border-white"
      />
      
      <div className={`relative bg-white border-2 ${borderColor} rounded-lg p-3 min-w-[160px] shadow-md transition-all duration-300 group-hover:shadow-lg`}
           style={{ boxShadow: hasViolations ? '0 4px 20px rgba(239, 68, 68, 0.15)' : '0 4px 15px rgba(0, 0, 0, 0.08)' }}>
        {/* Alert badge */}
        {hasViolations && (
          <div className="absolute -top-2 -right-2 px-2 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full shadow-md animate-pulse">
            {data.policyViolations} alerts
          </div>
        )}
        
        {/* Device icon and role */}
        <div className="flex items-center gap-2 mb-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.bg}`}>
            <svg className={`w-4 h-4 ${colors.text}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
          </div>
          <div>
            <div className="text-xs font-semibold text-slate-800">{data.role || 'Device'}</div>
            <div className="text-[9px] text-slate-500">DISCOVERED</div>
          </div>
        </div>
        
        {/* IP Address */}
        <div className="bg-slate-50 rounded px-2 py-1.5 mb-2 border border-slate-100">
          <div className={`text-sm font-mono font-semibold ${hasViolations ? 'text-red-600' : 'text-cyan-600'}`}>
            {data.ip}
          </div>
        </div>
        
        {/* Protocol badges */}
        {data.protocols && data.protocols.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-2">
            {data.protocols.map((protocol, idx) => {
              const pColor = protocolColors[protocol] || protocolColors.default;
              return (
                <span key={idx} className={`text-[9px] px-1.5 py-0.5 rounded border font-semibold uppercase ${pColor.badge}`}>
                  {protocol}
                </span>
              );
            })}
          </div>
        )}
        
        {/* Event count */}
        <div className="text-[10px] text-slate-500 text-center">
          {data.eventCount || 0} events
        </div>
      </div>
    </div>
  );
};

// Node types registry
const nodeTypes = {
  cloud: CloudNode,
  edgeGateway: EdgeGatewayNode,
  device: DeviceNode,
};

// ════════════════════════════════════════════════════════════════════════════
// EDGE NODE FILTER DROPDOWN - Light
// ════════════════════════════════════════════════════════════════════════════

const EdgeNodeFilter = ({ edgeNodes, selectedAgent, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const selectedNode = selectedAgent 
    ? edgeNodes.find(n => n.edge_node_id === selectedAgent)
    : null;
  
  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-300 rounded-lg px-4 py-2 text-sm text-slate-700 transition-colors min-w-[280px] shadow-sm"
      >
        <svg className="w-4 h-4 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        <span className="flex-1 text-left truncate">
          {selectedNode ? selectedNode.edge_node_id : 'All Edge Nodes'}
        </span>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full left-0 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-xl z-50 overflow-hidden">
            <div className="max-h-80 overflow-y-auto">
              <button
                onClick={() => { onSelect(null); setIsOpen(false); }}
                className={`w-full px-4 py-3 text-left text-sm hover:bg-slate-50 transition-colors flex items-center gap-2 ${
                  !selectedAgent ? 'bg-cyan-50 text-cyan-700' : 'text-slate-700'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
                All Edge Nodes
              </button>
              
              {[...edgeNodes]
                .sort((a, b) => (a.edge_node_id || '').localeCompare(b.edge_node_id || ''))
                .map(node => {
                  const status = getNodeStatus(node);
                  const isOnline = status === 'Online';
                  return (
                <button
                  key={node.edge_node_id}
                  onClick={() => { onSelect(node.edge_node_id); setIsOpen(false); }}
                  className={`w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors ${
                    selectedAgent === node.edge_node_id ? 'bg-cyan-50' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isOnline ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm font-medium truncate ${
                        selectedAgent === node.edge_node_id ? 'text-cyan-700' : 'text-slate-800'
                      }`}>
                        {node.edge_node_id}
                      </div>
                      {node.hostname && (
                        <div className="text-[11px] text-slate-500 truncate mt-0.5">
                          {node.hostname}
                        </div>
                      )}
                    </div>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                      isOnline 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-red-100 text-red-700'
                    }`}>
                      {status}
                    </span>
                  </div>
                </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// DETAIL PANEL COMPONENT - Light
// ════════════════════════════════════════════════════════════════════════════

const DetailPanel = ({ selectedNode, onClose }) => {
  if (!selectedNode) return null;
  
  return (
    <div className="absolute top-4 right-4 w-80 bg-white border border-slate-200 rounded-2xl shadow-2xl overflow-hidden z-50">
      <div className="bg-gradient-to-r from-cyan-500 to-blue-500 px-4 py-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">
          {selectedNode.type === 'edgeGateway' ? 'Edge Agent Details' : 
           selectedNode.type === 'device' ? 'Discovered Device' : 
           selectedNode.type === 'cloud' ? 'Management Plane' : 'Details'}
        </h3>
        <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <div className="p-4 space-y-4 max-h-[500px] overflow-y-auto">
        {selectedNode.type === 'cloud' && (
          <>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-cyan-100 border border-cyan-300">
                <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                </svg>
              </div>
              <div>
                <div className="text-lg font-semibold text-slate-800">{selectedNode.data.label}</div>
                <div className="text-xs text-slate-500">{selectedNode.data.sublabel}</div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <div className="text-[10px] text-slate-500 uppercase mb-1">Agents</div>
                <div className="text-xl font-bold text-emerald-600">{selectedNode.data.edgeCount || 0}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <div className="text-[10px] text-slate-500 uppercase mb-1">Devices</div>
                <div className="text-xl font-bold text-blue-600">{selectedNode.data.assetCount || 0}</div>
              </div>
            </div>
            
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-sm text-emerald-700">Cloud Connected</span>
              </div>
              <div className="text-xs text-slate-500 mt-1">Management plane operational</div>
            </div>
          </>
        )}
        
        {selectedNode.type === 'edgeGateway' && (
          <>
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                selectedNode.data.status === 'Online' 
                  ? 'bg-emerald-100 border border-emerald-300' 
                  : 'bg-red-100 border border-red-300'
              }`}>
                <svg className={`w-6 h-6 ${selectedNode.data.status === 'Online' ? 'text-emerald-600' : 'text-red-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" />
                </svg>
              </div>
              <div>
                <div className="text-lg font-semibold text-slate-800">{selectedNode.data.nodeId}</div>
                {selectedNode.data.hostname && selectedNode.data.hostname !== selectedNode.data.nodeId && (
                  <div className="text-xs text-slate-500">{selectedNode.data.hostname}</div>
                )}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 rounded-lg p-2 border border-slate-100">
                <div className="text-[10px] text-slate-500 uppercase">Status</div>
                <div className={`text-sm font-semibold ${selectedNode.data.status === 'Online' ? 'text-emerald-600' : 'text-red-600'}`}>
                  {selectedNode.data.status}
                </div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2 border border-slate-100">
                <div className="text-[10px] text-slate-500 uppercase">Devices</div>
                <div className="text-sm font-semibold text-blue-600">{selectedNode.data.deviceCount || 0}</div>
              </div>
            </div>
            
            {selectedNode.data.ipAddress && (
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <div className="text-[10px] text-slate-500 uppercase mb-1">IP Address</div>
                <div className="text-sm text-cyan-600 font-mono">{selectedNode.data.ipAddress}</div>
              </div>
            )}
            
            {selectedNode.data.lastHeartbeat && (
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <div className="text-[10px] text-slate-500 uppercase mb-1">Last Heartbeat</div>
                <div className="text-sm text-slate-700">
                  {new Date(selectedNode.data.lastHeartbeat).toLocaleString('en-US', {
                    timeZone: 'America/Chicago',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false
                  })}
                </div>
              </div>
            )}
            
            {selectedNode.data.version && (
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <div className="text-[10px] text-slate-500 uppercase mb-1">Agent Version</div>
                <div className="text-sm text-slate-700 font-mono">v{selectedNode.data.version}</div>
              </div>
            )}
          </>
        )}
        
        {selectedNode.type === 'device' && (
          <>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-blue-100 border border-blue-300">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <div>
                <div className="text-lg font-semibold text-slate-800">{selectedNode.data.role || 'Device'}</div>
                <div className="text-xs text-slate-500">Discovered via scanning</div>
              </div>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
              <div className="text-[10px] text-slate-500 uppercase mb-1">IP Address</div>
              <div className="text-lg text-cyan-600 font-mono font-semibold">{selectedNode.data.ip}</div>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 rounded-lg p-2 border border-slate-100">
                <div className="text-[10px] text-slate-500 uppercase">Events</div>
                <div className="text-sm font-semibold text-blue-600">{selectedNode.data.eventCount || 0}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2 border border-slate-100">
                <div className="text-[10px] text-slate-500 uppercase">Alerts</div>
                <div className={`text-sm font-semibold ${selectedNode.data.policyViolations > 0 ? 'text-red-600' : 'text-slate-500'}`}>
                  {selectedNode.data.policyViolations || 0}
                </div>
              </div>
            </div>
            
            {selectedNode.data.protocols && selectedNode.data.protocols.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <div className="text-[10px] text-slate-500 uppercase mb-2">Protocols Detected</div>
                <div className="flex flex-wrap gap-2">
                  {selectedNode.data.protocols.map((protocol, idx) => (
                    <span key={idx} className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded border border-blue-200 uppercase font-semibold">
                      {protocol}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {selectedNode.data.policies && selectedNode.data.policies.length > 0 && (
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-100">
                <div className="text-[10px] text-slate-500 uppercase mb-2">Policy Matches</div>
                <div className="space-y-1">
                  {selectedNode.data.policies.slice(0, 5).map((policy, idx) => (
                    <div key={idx} className="text-xs text-slate-700 bg-white rounded px-2 py-1 border border-slate-200">
                      {policy}
                    </div>
                  ))}
                  {selectedNode.data.policies.length > 5 && (
                    <div className="text-xs text-slate-500">+{selectedNode.data.policies.length - 5} more</div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════════════
// LEGEND COMPONENT - Light
// ════════════════════════════════════════════════════════════════════════════

const Legend = () => (
  <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3 shadow-lg">
    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Legend</div>
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-emerald-500" />
        <span className="text-xs text-slate-600">Agent Online</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-red-500" />
        <span className="text-xs text-slate-600">Agent Offline / Alerts</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-orange-400" />
        <span className="text-xs text-slate-600">Modbus Device</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-3 h-3 rounded bg-purple-400" />
        <span className="text-xs text-slate-600">OPC-UA Device</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-8 h-0.5 bg-gradient-to-r from-cyan-500 to-cyan-300" />
        <span className="text-xs text-slate-600">Data Flow</span>
      </div>
    </div>
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// STATS BAR COMPONENT - Light
// ════════════════════════════════════════════════════════════════════════════

const StatsBar = ({ stats }) => (
  <div className="flex items-center gap-4">
    <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-200 shadow-sm">
      <span className="text-emerald-600 font-bold text-lg">{stats.onlineAgents}</span>
      <span className="text-slate-500 text-xs">/ {stats.totalAgents} Online</span>
    </div>
    <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-slate-200 shadow-sm">
      <span className="text-blue-600 font-bold text-lg">{stats.totalDevices}</span>
      <span className="text-slate-500 text-xs">Devices</span>
    </div>
    {stats.alertCount > 0 && (
      <div className="flex items-center gap-2 bg-red-50 rounded-lg px-3 py-2 border border-red-200 animate-pulse">
        <span className="text-red-600 font-bold text-lg">{stats.alertCount}</span>
        <span className="text-red-500 text-xs">Alerts</span>
      </div>
    )}
  </div>
);

// ════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT - Light Theme
// ════════════════════════════════════════════════════════════════════════════

const NetworkTopologyLight = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [edgeNodesList, setEdgeNodesList] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [allEvents, setAllEvents] = useState([]);
  const [stats, setStats] = useState({
    totalAgents: 0,
    onlineAgents: 0,
    totalDevices: 0,
    alertCount: 0,
  });

  // Build topology from Edge Nodes + Events
  const buildTopology = useCallback((edgeNodes, events, filterAgentId = null) => {
    const newNodes = [];
    const newEdges = [];
    
    const filteredEdgeNodes = filterAgentId 
      ? edgeNodes.filter(n => n.edge_node_id === filterAgentId)
      : edgeNodes;
    
    const CLOUD_Y = 0;
    const EDGE_Y = 200;
    const DEVICE_Y_START = 420;
    const HORIZONTAL_GAP = filteredEdgeNodes.length > 4 ? 260 : filteredEdgeNodes.length > 3 ? 300 : 350;
    const DEVICE_GAP = 180;
    
    const devicesByAgent = {};
    let totalAlerts = 0;
    
    events.forEach(event => {
      const agentId = event.celerway_edge_node_id;
      const destIp = event.destination_ip;
      
      if (!agentId || !destIp) return;
      if (filterAgentId && agentId !== filterAgentId) return;
      
      if (!devicesByAgent[agentId]) {
        devicesByAgent[agentId] = {};
      }
      
      if (!devicesByAgent[agentId][destIp]) {
        devicesByAgent[agentId][destIp] = {
          ip: destIp,
          protocols: new Set(),
          eventCount: 0,
          policyViolations: 0,
          policies: new Set(),
          role: 'PLC',
        };
      }
      
      const device = devicesByAgent[agentId][destIp];
      device.eventCount++;
      
      if (event.protocol) {
        device.protocols.add(event.protocol.toLowerCase());
      }
      
      if (event.details?.policy_name) {
        device.policies.add(event.details.policy_name);
        if (event.severity === 'Warning' || event.severity === 'Critical' || event.severity === 'High') {
          device.policyViolations++;
          totalAlerts++;
        }
      }
      
      if (device.protocols.has('modbus') || device.protocols.has('opcua')) {
        device.role = 'PLC';
      }
    });
    
    let totalDevices = 0;
    Object.values(devicesByAgent).forEach(devices => {
      totalDevices += Object.keys(devices).length;
    });
    
    const totalWidth = Math.max(filteredEdgeNodes.length * HORIZONTAL_GAP, 600);
    const startX = -totalWidth / 2 + HORIZONTAL_GAP / 2;
    
    // Cloud node
    newNodes.push({
      id: 'cloud',
      type: 'cloud',
      position: { x: 0, y: CLOUD_Y },
      data: {
        label: 'AI Sentry Cloud',
        sublabel: 'ai-sentry.com',
        edgeCount: filteredEdgeNodes.length,
        assetCount: totalDevices,
      },
    });
    
    // Edge nodes and their devices
    filteredEdgeNodes.forEach((edgeNode, index) => {
      const edgeX = startX + index * HORIZONTAL_GAP;
      const nodeId = edgeNode.edge_node_id;
      const status = getNodeStatus(edgeNode);
      
      const agentDevices = devicesByAgent[nodeId] || {};
      const deviceCount = Object.keys(agentDevices).length;
      
      newNodes.push({
        id: `edge-${nodeId}`,
        type: 'edgeGateway',
        position: { x: edgeX, y: EDGE_Y },
        data: {
          nodeId: nodeId,
          hostname: edgeNode.hostname,
          status: status,
          ipAddress: edgeNode.ip_address,
          version: edgeNode.agent_version,
          lastHeartbeat: edgeNode.last_heartbeat,
          deviceCount: deviceCount,
        },
      });
      
      // Edge from cloud to edge node
      newEdges.push({
        id: `cloud-to-${nodeId}`,
        source: 'cloud',
        target: `edge-${nodeId}`,
        animated: status === 'Online',
        style: {
          stroke: status === 'Online' ? '#22d3ee' : '#f87171',
          strokeWidth: 2,
        },
      });
      
      // Device nodes
      const deviceList = Object.values(agentDevices);
      const deviceStartX = edgeX - ((deviceList.length - 1) * DEVICE_GAP) / 2;
      
      deviceList.forEach((device, deviceIndex) => {
        const deviceX = deviceStartX + deviceIndex * DEVICE_GAP;
        const deviceId = `device-${nodeId}-${device.ip}`;
        
        newNodes.push({
          id: deviceId,
          type: 'device',
          position: { x: deviceX, y: DEVICE_Y_START + Math.random() * 40 },
          data: {
            ip: device.ip,
            protocols: Array.from(device.protocols),
            eventCount: device.eventCount,
            policyViolations: device.policyViolations,
            policies: Array.from(device.policies),
            role: device.role,
          },
        });
        
        newEdges.push({
          id: `${nodeId}-to-${device.ip}`,
          source: `edge-${nodeId}`,
          target: deviceId,
          animated: true,
          style: {
            stroke: device.policyViolations > 0 ? '#f87171' : '#60a5fa',
            strokeWidth: 2,
          },
        });
      });
    });
    
    return { 
      nodes: newNodes, 
      edges: newEdges, 
      stats: {
        totalAgents: filteredEdgeNodes.length,
        onlineAgents: filteredEdgeNodes.filter(n => isNodeOnline(n)).length,
        totalDevices,
        alertCount: totalAlerts,
      }
    };
  }, []);

  // Fetch data
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [edgeNodesResponse, eventsResponse] = await Promise.all([
        edgeNodesService.getEdgeNodes(),
        eventsService.getEvents({ limit: 500 }),
      ]);
      
      const edgeNodes = edgeNodesResponse.data || edgeNodesResponse || [];
      const events = eventsResponse.data || eventsResponse || [];
      
      setEdgeNodesList(edgeNodes);
      setAllEvents(events);
      
      const validAgent = selectedAgent && edgeNodes.find(n => n.edge_node_id === selectedAgent) ? selectedAgent : null;
      const agentToUse = validAgent || (edgeNodes.length > 0 ? edgeNodes[0].edge_node_id : null);

      if (!validAgent && edgeNodes.length > 0) {
        setSelectedAgent(agentToUse);
      }
      
      const { nodes: newNodes, edges: newEdges, stats: newStats } = buildTopology(edgeNodes, events, agentToUse);
      
      setNodes(newNodes);
      setEdges(newEdges);
      setStats(newStats);
      
    } catch (err) {
      console.error('Failed to fetch topology data:', err);
      setError(err.message || 'Failed to load network topology');
    } finally {
      setLoading(false);
    }
  }, [buildTopology, setNodes, setEdges, selectedAgent, setSelectedAgent]);

  // Rebuild topology when filter changes
  useEffect(() => {
    if (edgeNodesList.length > 0) {
      const { nodes: newNodes, edges: newEdges, stats: newStats } = buildTopology(edgeNodesList, allEvents, selectedAgent);
      setNodes(newNodes);
      setEdges(newEdges);
      setStats(newStats);
    }
  }, [selectedAgent, edgeNodesList, allEvents, buildTopology, setNodes, setEdges]);

  // Initial fetch and refresh interval
  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 180000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Handle node click
  const onNodeClick = useCallback((event, node) => {
    setSelectedNode(node);
  }, []);

  return (
    <div className="h-[calc(100vh-64px)] w-full bg-white relative overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(0,0,0,0.03) 1px, transparent 0)`,
          backgroundSize: '40px 40px',
        }} />
      </div>
      
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-40 bg-gradient-to-b from-white via-white to-transparent pb-8 px-6 pt-4">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
              <span className="w-10 h-10 bg-gradient-to-br from-cyan-100 to-blue-100 rounded-xl flex items-center justify-center border border-cyan-300">
                <svg className="w-6 h-6 text-cyan-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              </span>
              Network Topology
            </h1>
            <p className="text-slate-500 text-sm mt-1 ml-13">Real-time OT infrastructure • Auto-discovered from events</p>
            
            {/* Edge Node Filter */}
            <div className="mt-3">
              <EdgeNodeFilter 
                edgeNodes={edgeNodesList}
                selectedAgent={selectedAgent}
                onSelect={setSelectedAgent}
              />
            </div>
          </div>
          <StatsBar stats={stats} />
        </div>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/90">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-cyan-200 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4" />
            <div className="text-cyan-600 text-lg font-medium">Building topology...</div>
            <div className="text-slate-400 text-sm mt-1">Analyzing events and discovering devices</div>
          </div>
        </div>
      )}
      
      {/* Error state */}
      {error && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-white/90">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 max-w-md text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="text-red-700 text-lg font-medium mb-2">Failed to load topology</div>
            <div className="text-slate-600 text-sm">{error}</div>
          </div>
        </div>
      )}
      
      {/* React Flow canvas */}
      {!loading && !error && (
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.4, includeHiddenNodes: false }}
          minZoom={0.2}
          maxZoom={1.5}
          defaultViewport={{ x: 0, y: 0, zoom: 0.7 }}
          proOptions={{ hideAttribution: true }}
        >
          <Background 
            variant="dots" 
            gap={20} 
            size={1} 
            color="rgba(0, 0, 0, 0.05)" 
          />
          <Controls 
            className="!bg-white !border-slate-200 !rounded-xl !shadow-lg overflow-hidden [&>button]:!bg-white [&>button]:!border-slate-200 [&>button]:!text-slate-600 [&>button:hover]:!bg-slate-50 [&>button:hover]:!text-slate-800"
          />
          <MiniMap 
            nodeColor={(node) => {
              if (node.type === 'cloud') return '#06b6d4';
              if (node.type === 'edgeGateway') return node.data?.status === 'Online' ? '#10b981' : '#ef4444';
              return '#3b82f6';
            }}
            maskColor="rgba(255, 255, 255, 0.8)"
            className="!bg-white !border-slate-200 rounded-xl !shadow-lg"
          />
          <Panel position="bottom-left">
            <Legend />
          </Panel>
        </ReactFlow>
      )}
      
      {/* Detail panel */}
      <DetailPanel 
        selectedNode={selectedNode} 
        onClose={() => setSelectedNode(null)} 
      />
    </div>
  );
};

export default NetworkTopologyLight;
